import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SKIP_AUTH_INTERCEPTOR } from '../http-context-tokens';
import { AuthService } from './auth.service';
import { AppInsightsService } from '../../services/app-insights.service';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  public router = inject(Router);
  constructor(private authService: AuthService) { }

  private isRefreshingToken = false;
  private refreshedToken$ = new BehaviorSubject<string | null>(null);
  private readonly tabId = this.getOrCreateTabId();
  private readonly refreshLockKey = 'conviva_refresh_lock_v1';
  private readonly refreshLockTtlMs = 30_000;
  private bc?: BroadcastChannel;

  private getOrCreateTabId(): string {
    try {
      const k = 'conviva_tab_id_v1';
      const existing = sessionStorage.getItem(k);
      if (existing) return existing;
      const id =
        (globalThis.crypto as any)?.randomUUID?.() ??
        `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      sessionStorage.setItem(k, id);
      return id;
    } catch {
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  }

  private initCrossTabListenersOnce(): void {
    // Inicializa uma vez por instância (guard via bc undefined).
    if (this.bc !== undefined) return;

    try {
      if (typeof BroadcastChannel !== 'undefined') {
        this.bc = new BroadcastChannel('conviva-auth');
        this.bc.onmessage = (ev: MessageEvent) => {
          const data = ev.data;
          if (data?.type === 'token_refreshed' && typeof data?.token === 'string') {
            this.refreshedToken$.next(data.token);
          }
        };
      } else {
        this.bc = undefined;
      }
    } catch {
      this.bc = undefined;
    }

    try {
      // Fallback: storage event para browsers sem BroadcastChannel (ou bloqueados).
      window.addEventListener('storage', (e: StorageEvent) => {
        if (e.key === 'tokenConviva' && typeof e.newValue === 'string' && e.newValue.length > 0) {
          this.refreshedToken$.next(e.newValue);
        }
        if (e.key === this.refreshLockKey && e.newValue == null) {
          // lock liberado: nada a fazer, o token pode ter sido renovado.
        }
      });
    } catch {
      // ignore
    }
  }

  private tryAcquireRefreshLock(): boolean {
    try {
      const now = Date.now();
      const raw = localStorage.getItem(this.refreshLockKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { owner: string; ts: number };
        if (parsed?.ts && now - parsed.ts < this.refreshLockTtlMs) {
          return parsed.owner === this.tabId; // já sou o dono
        }
      }
      localStorage.setItem(this.refreshLockKey, JSON.stringify({ owner: this.tabId, ts: now }));
      return true;
    } catch {
      // Sem storage disponível, assume lock adquirido para não bloquear.
      return true;
    }
  }

  private releaseRefreshLock(): void {
    try {
      const raw = localStorage.getItem(this.refreshLockKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { owner: string; ts: number };
      if (parsed?.owner === this.tabId) {
        localStorage.removeItem(this.refreshLockKey);
      }
    } catch {
      // ignore
    }
  }

  private broadcastTokenRefreshed(token: string): void {
    try {
      this.bc?.postMessage?.({ type: 'token_refreshed', token, by: this.tabId });
    } catch {
      // ignore
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.initCrossTabListenersOnce();

    if (req.context.get(SKIP_AUTH_INTERCEPTOR) || req.url.includes('version.json')) {
      return next.handle(req);
    }

    const token = this.authService.getToken();

    const codigoEscola = this.authService.getEscola();
    const codigoDiretoria = this.authService.getDiretoria();

    if (token != null && token != "" && token != undefined) {
      const isSed = req.url.includes('/credenciais');
      const isGovbr = req.url.includes('idpsp');
      const isDiario = req.url.includes('diarioclasse');

      if (isSed) {
        const isCyberark = req.url.toLowerCase().includes('cyberark');

        const headers = new HttpHeaders({
          'Authorization': isCyberark ? this.authService.getTokenMasp() : this.authService.getTokenGovBr(),
          'Ocp-Apim-Subscription-Key': environment.Apim,
          'Access-Control-Allow-Origin': environment.baseFrontUrl
        });

        req = req.clone({
          withCredentials: false,
          headers: headers
        });

        return next.handle(req);
      }
      else {

        if (isGovbr) {
          return next.handle(req);
        }

        if (isDiario) {
          const headers = new HttpHeaders({
            'Authorization': token,
            'Ocp-Apim-Subscription-Key': environment.diarioKey,
            'Access-Control-Allow-Origin': environment.baseFrontUrl
          });

          req = req.clone({
            withCredentials: false,
            headers: headers
          });

          return next.handle(req);
        }
        else {
          const headersConfig: { [header: string]: string } = {
            'Authorization': token,
            'Ocp-Apim-Subscription-Key': environment.ApimConviva
          };

          if (codigoEscola)
            headersConfig['X-Codigo-Escola'] = codigoEscola;

          if (codigoDiretoria) 
            headersConfig['X-Codigo-Diretoria'] = codigoDiretoria;

          const headers = new HttpHeaders(headersConfig);

          req = req.clone({
            withCredentials: false,
            headers: headers
          });

          return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
              if (error.status !== 401) {
                return throwError(() => error);
              }

              // Evita loop de refresh ao chamar endpoints de token/login.
              const url = req.url?.toLowerCase?.() ?? '';
              const isTokenEndpoint =
                url.includes('/api/login/') ||
                url.includes('/api/sed/') ||
                url.includes('gettokensed') ||
                url.includes('gettokensedmasp');
              if (isTokenEndpoint) {
                this.router.navigate(['/logout']);
                return throwError(() => error);
              }

              return this.handle401AndRetry(req, next, error);
            })
          );
        }
      }
    }

    return next.handle(req);
  }

  private handle401AndRetry(
    req: HttpRequest<any>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.refreshedToken$.next(null);

      // Multi-abas: evita tempestade de refresh. Se outra aba estiver com lock válido,
      // aguardamos o token atualizado via BroadcastChannel/storage event.
      if (!this.tryAcquireRefreshLock()) {
        this.isRefreshingToken = false;
        return this.refreshedToken$.pipe(
          filter((t): t is string => typeof t === 'string' && t.length > 0),
          take(1),
          switchMap((t) => {
            const headers = req.headers.set('Authorization', t);
            const retryReq = req.clone({ headers });
            return next.handle(retryReq);
          })
        );
      }

      return this.authService.getTokenConviva().pipe(
        switchMap((resp: any) => {
          // Esperado: API retorna token em `data` (string) ou em `data.token`.
          const newToken =
            resp?.data?.tokenConsumoConteudo ??
            resp?.data?.token ??
            resp?.data?.accessToken ??
            resp?.data ??
            null;

          if (!newToken || typeof newToken !== 'string') {
            this.isRefreshingToken = false;
            this.releaseRefreshLock();
            this.router.navigate(['/logout']);
            return throwError(() => originalError);
          }

          this.authService.setTokenConviva(newToken);
          this.isRefreshingToken = false;
          this.refreshedToken$.next(newToken);
          this.broadcastTokenRefreshed(newToken);
          this.releaseRefreshLock();

          const headers = req.headers.set('Authorization', newToken);
          const retryReq = req.clone({ headers });
          return next.handle(retryReq);
        }),
        catchError((err: any) => {
          this.isRefreshingToken = false;
          this.releaseRefreshLock();
          this.router.navigate(['/logout']);
          return throwError(() => err);
        })
      );
    }

    // Já existe refresh em andamento; aguarda token e refaz request uma vez.
    return this.refreshedToken$.pipe(
      filter((t): t is string => typeof t === 'string' && t.length > 0),
      take(1),
      switchMap((t) => {
        const headers = req.headers.set('Authorization', t);
        const retryReq = req.clone({ headers });
        return next.handle(retryReq);
      })
    );
  }
}
